import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import {
  AlertInlineComponent,
  AppUpdateBannerComponent,
  MainService,
  NavbarComponent,
  ToasterService,
  AuthService,
  NotSupportedScreenComponent,
  MaintenanceModeComponent,
  SiteRedirectNoticeComponent,
  appUrlChanges,
  isVendorPortalUrl,
  resolveAppUrl,
} from '@kuiper/ibusiness-core';

@Component({
  imports: [
    RouterOutlet,
    NavbarComponent,
    AlertInlineComponent,
    NotSupportedScreenComponent,
    MaintenanceModeComponent,
    SiteRedirectNoticeComponent,
    AppUpdateBannerComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AppComponent {
  private readonly toastService = inject(ToasterService);
  readonly authService = inject(AuthService);
  private readonly titleService = inject(Title);
  private readonly transloco = inject(TranslocoService);
  readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly mainService = inject(MainService);
  private readonly currentUrl = toSignal(appUrlChanges(this.router), {
    initialValue: resolveAppUrl(this.router.url),
  });
  protected readonly sideMenuShown = computed(() =>
    this.mainService.sideMenuShown()
  );

  toastData = computed(() => this.toastService.toasterData());
  showToast = computed(() => this.toastService.showToast());
  isLoggedIn = computed(() => this.authService.isLoggedIn());
  isLogoutLoading = computed(() => this.authService.isLogoutLoading());
  isVendor = computed(() => this.authService.isVendor());
  selectedWorkspace = computed(() => this.authService.selectedWorkspace());
  showNavbar = computed(
    () => (this.isLoggedIn() || this.isLogoutLoading()) && !this.sideMenuShown()
  );
  isOnVendorPortal = computed(() => isVendorPortalUrl(this.currentUrl() ?? ''));

  constructor() {
    // Set initial app title
    this.setAppTitle();

    // Update title when language changes
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setAppTitle();
      });
  }

  private setAppTitle(): void {
    this.transloco
      .selectTranslate('project_name')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translatedTitle: string) => {
        const title =
          this.selectedWorkspace()?.name ??
          this.selectedWorkspace()?.slug ??
          '';
        this.titleService.setTitle(title || translatedTitle);
      });
  }

  onCloseToast($event: boolean) {
    this.toastService.showToast.set($event);
    this.toastService.resetInterval();
  }
}
